/**
 * Admin Support Mode (impersonation) helpers.
 * The presence of `impersonate_org_id` in sessionStorage causes apiCall() to attach
 * the X-Impersonate-Org-Id header to every request, making the backend treat the
 * current admin as the target employer/agency.
 */

import { apiCall } from './api';

const KEY_ID = 'impersonate_org_id';
const KEY_ORG = 'impersonate_org';

export function getImpersonatedOrg() {
  try {
    const raw = sessionStorage.getItem(KEY_ORG);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function isImpersonating() {
  return !!sessionStorage.getItem(KEY_ID);
}

export async function startImpersonation(org, opts = {}) {
  const mode = opts.mode || 'consent'; // 'consent' | 'forced' | 'legacy'
  const reason = (opts.reason || '').trim();
  const scopes = Array.isArray(opts.scopes) && opts.scopes.length > 0 ? opts.scopes : ['all'];
  if (mode === 'consent') {
    const r = await apiCall(`/api/admin/impersonate/request/${org._id}`, {
      method: 'POST',
      body: JSON.stringify({ reason, scopes }),
    });
    return { status: 'pending', session: r.session };
  }
  if (mode === 'forced') {
    if (reason.length < 5) throw new Error('Forced entry requires a written reason.');
    await apiCall(`/api/admin/impersonate/force/${org._id}`, {
      method: 'POST',
      body: JSON.stringify({ reason, scopes }),
    });
  } else {
    await apiCall(`/api/admin/impersonate/start/${org._id}`, { method: 'POST' });
  }
  sessionStorage.setItem(KEY_ID, org._id);
  sessionStorage.setItem(KEY_ORG, JSON.stringify({
    _id: org._id,
    full_name: org.full_name || org.email || '',
    email: org.email || '',
    role: org.role || 'employer',
    company_name: org.company_name || '',
    mode,
    scopes,
  }));
  window.dispatchEvent(new Event('impersonation-changed'));
  return { status: 'granted' };
}

export async function stopImpersonation() {
  const orgId = sessionStorage.getItem(KEY_ID);
  sessionStorage.removeItem(KEY_ID);
  sessionStorage.removeItem(KEY_ORG);
  // Best-effort audit ping (after we've removed the header so this fires as the admin)
  try {
    await apiCall('/api/admin/impersonate/stop', {
      method: 'POST',
      body: JSON.stringify({ org_id: orgId }),
    });
  } catch {
    // non-blocking
  }
  window.dispatchEvent(new Event('impersonation-changed'));
}

export function onImpersonationChange(callback) {
  const handler = () => callback(getImpersonatedOrg());
  window.addEventListener('impersonation-changed', handler);
  return () => window.removeEventListener('impersonation-changed', handler);
}
